import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Spinner, Alert, AlertIcon, Button, Link, Code,
  Table, Thead, Tbody, Tr, Th, Td, Tooltip, IconButton, useToast,
} from '@chakra-ui/react';
import { FiEye, FiEyeOff, FiCopy, FiExternalLink, FiShield } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

const API_BASE = '/api/v1';
// How long a revealed password stays on screen.
export const REVEAL_SECONDS = 30;

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function CredentialRow({ service, username, revealed, onReveal, onHide, revealing }) {
  const toast = useToast();
  const id = `${service.key}/${username}`;
  const copy = () => {
    navigator.clipboard?.writeText(revealed).then(
      () => toast({ status: 'success', title: 'Password copied', duration: 2000 }),
      () => toast({ status: 'error', title: 'Could not copy' }),
    );
  };
  return (
    <Tr>
      <Td>
        <HStack spacing={2}>
          <Text fontWeight="medium">{service.name}</Text>
          {service.url && (
            <Link href={service.url} isExternal aria-label={`Open ${service.name}`}>
              <FiExternalLink />
            </Link>
          )}
        </HStack>
      </Td>
      <Td><Code>{username}</Code></Td>
      <Td>
        {revealed ? (
          <HStack spacing={2}>
            <Code data-testid={`password-${id}`}>{revealed}</Code>
            <Tooltip label="Copy">
              <IconButton size="xs" variant="ghost" aria-label="Copy password" icon={<FiCopy />} onClick={copy} />
            </Tooltip>
            <Tooltip label="Hide">
              <IconButton size="xs" variant="ghost" aria-label="Hide password" icon={<FiEyeOff />} onClick={() => onHide(id)} />
            </Tooltip>
          </HStack>
        ) : (
          <Button
            size="xs"
            variant="outline"
            leftIcon={<FiEye />}
            onClick={() => onReveal(service.key, username)}
            isLoading={revealing === id}
          >
            Reveal
          </Button>
        )}
      </Td>
    </Tr>
  );
}

function ServiceCredentials() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revealed, setRevealed] = useState({}); // "service/username" → password
  const [revealing, setRevealing] = useState(null);
  const timers = useRef({});
  const toast = useToast();

  useEffect(() => {
    fetch(`${API_BASE}/service-credentials/`, { headers: getAuthHeaders() })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (res.status === 403) throw new Error('Only super admins can see service credentials.');
        if (res.status === 404) throw new Error(body.reason || 'Service credentials are only available on AWS.');
        if (!res.ok && !body.error) throw new Error(`HTTP ${res.status}`);
        return body;
      })
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    const pending = timers.current;
    return () => Object.values(pending).forEach(clearTimeout);
  }, []);

  const hide = useCallback((id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setRevealed((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const reveal = (service, username) => {
    const id = `${service}/${username}`;
    setRevealing(id);
    fetch(`${API_BASE}/service-credentials/reveal/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ service, username }),
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || body.reason || `HTTP ${res.status}`);
        return body;
      })
      .then(({ password }) => {
        setRevealed((prev) => ({ ...prev, [id]: password }));
        clearTimeout(timers.current[id]);
        timers.current[id] = setTimeout(() => hide(id), REVEAL_SECONDS * 1000);
      })
      .catch((err) => toast({ status: 'error', title: 'Could not reveal password', description: err.message }))
      .finally(() => setRevealing(null));
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" /></Box>;
  if (error) return <Alert status="warning" borderRadius="lg"><AlertIcon />{error}</Alert>;

  const rows = (data.services || []).flatMap((service) => (
    service.usernames.length
      ? service.usernames.map((username) => ({ service, username }))
      : [{ service, username: null }]
  ));

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="lg">Service Credentials</Heading>
        <Text color="gray.500" fontSize="sm">
          Django admin logins for Selwyn's microservices, read from AWS Secrets Manager
          {data.secret_id && <> (<Code fontSize="xs">{data.secret_id}</Code>)</>}.
        </Text>
      </Box>

      <Alert status="info" borderRadius="lg">
        <AlertIcon as={FiShield} />
        Every reveal is recorded — who, which login and from where — in the{' '}
        <Link as={RouterLink} to="/audit-log" fontWeight="semibold" ml={1}>Audit Log</Link>.
        Revealed passwords hide again after {REVEAL_SECONDS} seconds.
      </Alert>

      {data.error && <Alert status="error" borderRadius="lg"><AlertIcon />{data.error}</Alert>}

      {rows.length > 0 && (
        <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
          <Table size="sm">
            <Thead><Tr><Th>Service</Th><Th>Username</Th><Th>Password</Th></Tr></Thead>
            <Tbody>
              {rows.map(({ service, username }) => (username ? (
                <CredentialRow
                  key={`${service.key}/${username}`}
                  service={service}
                  username={username}
                  revealed={revealed[`${service.key}/${username}`]}
                  onReveal={reveal}
                  onHide={hide}
                  revealing={revealing}
                />
              ) : (
                <Tr key={service.key}>
                  <Td><Text fontWeight="medium">{service.name}</Text></Td>
                  <Td colSpan={2}><Text fontSize="sm" color="gray.500">No stored login — rotate this service's passwords to add one.</Text></Td>
                </Tr>
              )))}
            </Tbody>
          </Table>
        </Box>
      )}
    </VStack>
  );
}

export default ServiceCredentials;
